import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import GuideLayout from "./GuideLayout";

const META_DESC =
  "Learn how to find GitHub developers by skill, location, and activity: search intent, advanced filters, followers, language, and last push.";

const GuideFindDevelopers = () => {
  useEffect(() => {
    const script = document.createElement("script");
    script.id = "gf-article-find-dev-jsonld";
    script.type = "application/ld+json";
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "TechArticle",
      headline: "How to find GitHub developers by skill, location, and activity",
      description: META_DESC,
      author: { "@type": "Organization", name: "GitHub Finder" },
    });
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById("gf-article-find-dev-jsonld");
      if (el) el.remove();
    };
  }, []);

  return (
    <GuideLayout
      title="How to find GitHub developers"
      description={META_DESC}
      eyebrow="Developer discovery"
      breadcrumb={[
        { to: "/", label: "Home" },
        { to: "/guide", label: "Guides" },
        { label: "Find developers" },
      ]}
    >
      <article className="gf-guide-prose" itemScope itemType="https://schema.org/TechArticle">
        <meta itemProp="headline" content="How to find GitHub developers by skill, location, and activity" />
        <p className="gf-guide-prose__lead">
          Whether you are recruiting, researching open source, or comparing portfolios, a structured{" "}
          <strong>GitHub Finder</strong> workflow beats random browsing. Start with a clear intent: use a{" "}
          <strong>username</strong> if you know it; otherwise use a <strong>skill or topic keyword</strong> (framework,
          library, or domain), and optionally a <strong>location</strong> to narrow results.
        </p>

        <h2>1. Define search intent</h2>
        <p>
          Ambiguous queries return noisy results. Decide whether you care about <strong>reach</strong> (followers,
          stars), <strong>specialisation</strong> (primary language, topics), or <strong>recency</strong> (last push).
          That choice drives which filters you enable first.
        </p>

        <h2>2. Main search + advanced filters</h2>
        <p>
          Enter keywords that appear in bios, names, or public metadata. Open <strong>Advanced filters</strong> to add{" "}
          <strong>location</strong>, <strong>minimum followers</strong>, a primary <strong>programming language</strong>
          , and constraints such as <strong>last push</strong> so you favour active maintainers. Qualifiers are applied
          in line with GitHub&apos;s search rules so you get relevant matches rather than generic lists.
        </p>

        <h2>3. Read the results and insights</h2>
        <p>
          Open a profile to see <strong>repository cards</strong> with stars, forks, languages, and last updated
          dates. On developer mode, use the <strong>insights panel</strong> where available to see language mix and
          highlighted projects—useful context when you <strong>search GitHub repositories</strong> in the context of a
          single maintainer.
        </p>

        <h2>4. Related guides</h2>
        <ul>
          <li>
            <Link to="/guide/search-github-repositories">Search GitHub repositories</Link> — repository-wide keyword
            search and sorting.
          </li>
          <li>
            <Link to="/guide/faq">FAQ</Link> — practical search questions and answers.
          </li>
        </ul>

        <p className="gf-guide-prose__cta">
          <Link to="/" className="gf-btn gf-btn--primary gf-btn--sm">
            Try the finder
          </Link>
        </p>
      </article>
    </GuideLayout>
  );
};

export default GuideFindDevelopers;
